import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { metrics } from '../metrics/client';

/**
 * Emits a clickstream `navigation` event on every SPA route change.
 *
 * Tracks route transitions within the single-page app and fires
 * `metrics.navigationEvent()` with the new path and the previous path
 * as metadata. The initial page load is intentionally skipped — only
 * subsequent in-app navigations are recorded.
 *
 * Must be rendered inside `<BrowserRouter>` (requires router context
 * for `useLocation()`).
 *
 * @example
 * // Inside BrowserRouter:
 * function RouteMetricsTracker() {
 *   useRouteMetrics();
 *   return null;
 * }
 */
export const useRouteMetrics = () => {
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);
  const isInitialMount = useRef(true);

  useEffect(() => {
    // Skip the initial mount — the first render is a page load, not a navigation.
    // A separate `page_view` event type can be added later for landing page tracking.
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const currentPath = location.pathname;

    if (prevPathRef.current !== currentPath) {
      metrics.add(metrics.navigationEvent(currentPath, {
        from: prevPathRef.current,
      }));
      prevPathRef.current = currentPath;
    }
  }, [location.pathname]);
};
